/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import Configuration, {ImsInfo, SettingsMethodMap} from './model/Configuration';
import {expand} from './utils/GenericUtil';
import Request from './model/Request';
import SETTINGS_LEVEL from './SettingsLevel';
import Settings from './model/GenericDataType';
import SettingsResponse from './model/Response';
import settingsConfig from './config/SettingsConfig.json';

export default class SettingsService {
  public settingsEndpoint: string;
  public imsInfo: ImsInfo;
  public settingsMap: SettingsMethodMap;
  private static settingsV2: string = 'settingsV2';
  private static settingsLevels: string[] = Object.values(SETTINGS_LEVEL);

  /**
   * @description Constructor for UserService class.
   *
   * @param {Object} configuration -  Config to initialize settings service.
   */
  constructor(configuration: Configuration) {
    if (!configuration) {
      throw new Error(`Init Settings Service failed: Missing configuration`);
    }

    const {env, imsInfo} = configuration;
    this.settingsEndpoint = settingsConfig.endpoints[env];
    this.imsInfo = imsInfo;
    this.readSettings = this.readSettings.bind(this);
    this.writeSettings = this.writeSettings.bind(this);
    this.deleteSettings = this.deleteSettings.bind(this);
    this.settingsMap = {
      DELETE: this.deleteSettings,
      GET: this.readSettings,
      POST: this.writeSettings
    };
  }

  public async settingsHandler({appId, data, imsInfo, imsOrgId, method}: Request)
    : Promise<SettingsResponse> {
    if (!method || !(method in this.settingsMap)) {
      return Promise.reject(`Incorrect method: ${method} requested by app: ${appId}. Supported: GET, POST, DELETE`);
    }
    const {settings} = data;
    const {error, opts, url} = this.processParams({appId, data, imsInfo, imsOrgId, method});
    if (error) {
      return Promise.reject(error);
    }

    try {
      let response = await fetch(decodeURIComponent(url.href), opts);
      if (response.status === 414) {// Request URI Too Long, retry passing Authorization in header
        let urlQueryParams = new URLSearchParams(url.search);
        urlQueryParams.delete('user_token');
        url.search = urlQueryParams.toString();

        response = await fetch(decodeURIComponent(url.href),
          Object.assign(opts, {headers: { Authorization: `Bearer ${this.imsInfo.accessToken}`}}));
      }
      return this.settingsMap[method.toUpperCase()](response, settings);
    } catch(NetworkError) {
      return Promise.reject(`Settings Service ${method} Request failed - appId ${appId}, error - ${NetworkError}`);
    }
  }

  /**
   * @description Method to process read settings.
   *
   * @param {Response} response containing appId, groupId, level, userId, orgId, settings.
   * @returns {Promise<SettingsResponse>} SettingsResponse body.
   */
  private async readSettings(response: Response, settings: Settings): Promise<SettingsResponse> {
    const contentType = response.headers.get('content-type');
    if (response.ok && contentType && contentType.includes('application/json')) {
      const data = await response.json();
      const keys = Object.keys(settings);
      keys && keys.forEach((key: string) => {
        if (!data.settings[key]) {// override with default value if value is undefined/null
          data.settings[key] = settings[key];
        }
      });
      return Promise.resolve({settings: data.settings});
    } else {
      return Promise.resolve({settings});
    }
  }

  /**
   * @description Method to process write settings.
   *
   * @param {Response} response containing appId, groupId, level, userId, orgId, createdKeys, updatedKeys.
   * @returns {Promise<SettingsResponse>} SettingsResponse body.
   */
  private async writeSettings(response: Response): Promise<SettingsResponse> {
    const contentType = response.headers.get('content-type');
    if (response.ok && contentType && contentType.includes('application/json')) {
      const data = await response.json();
      return Promise.resolve({settings: Object.assign(data.created, data.updated)});
    } else {
      return Promise.resolve({settings: {}});
    }
  }

  /**
   * @description Method to process delete settings.
   *
   * @param {Response} response containing appId, groupId, level, userId, orgId, deletedKeys.
   * @returns {Promise<SettingsResponse>} SettingsResponse body.
   */
  private async deleteSettings(response: Response): Promise<SettingsResponse> {
    const contentType = response.headers.get('content-type');
    if (response.ok && contentType && contentType.includes('application/json')) {
      const data = await response.json();
      return Promise.resolve({settings: data.deleted});
    } else {
      return Promise.resolve({settings: {}});
    }
  }

  /**
   * @description Method to get url and opts for fetch call.
   *
   * @param {Request} request containing appId, data, imsinfo, imsOrgId.
   * @returns {error?: string; opts: RequestInit; url: URL} {error, opts, url}. - error in case any.
   */
  private processParams({appId, data, imsInfo, imsOrgId, method} : Request)
    : {error?: string; opts: RequestInit; url: URL} {
    let {groupId, level, settings} = data;
    level = level || SETTINGS_LEVEL.USERORG;

    // Validations for correct request params
    if(!SettingsService.settingsLevels.includes(level) || !groupId || !appId || !settings) {
      return {error: `Malformed settings ${method} request: appId: ${appId}, groupId: ${groupId}, settings level: ${
        level}, settings: ${settings}`, opts: {}, url: new URL('https://error')};
    }
    //update ims info if ims client ID, access token are passed
    const imsData = imsInfo || this.imsInfo;
    let url = new URL(expand(this.settingsEndpoint, SettingsService.settingsV2, {level, appId, groupId}));
    const urlQueryParams = new URLSearchParams();
    urlQueryParams.append('user_token', imsData.accessToken);
    urlQueryParams.append('api_key', imsData.clientId);
    urlQueryParams.append('imsOrgId', imsOrgId);
    if (method != 'POST') {
      const keys = Object.keys(settings);
      keys && keys.forEach((key: string) => {
        urlQueryParams.append('keys', key);
      });
    }
    url.search = urlQueryParams.toString();

    let opts: RequestInit = Object.assign({method});
    if (method === 'POST') {
      opts.body = JSON.stringify(settings);
      opts.headers = {
        "Content-Type": "text/plain"
      };
    }
    return {opts, url};
  }
}
